/**
 * Feature flags — the single source of truth for `beta-feature-*` user tags.
 *
 * A user tag in `user.tags` gates a beta feature. Both Platform (React) and
 * Super (API gates + Classic console) read the constants below — import the
 * `*_TAG` constant rather than writing the string literal.
 *
 * To add a flag: add a `*_TAG` constant and a `FEATURE_FLAGS` entry — it then
 * shows up on the Classic feature-flags page (/users/:username/feature-flags)
 * and in seeded beta users automatically.
 */

/** Legacy umbrella beta flag. A few older gates still accept it; new features use their own granular tag below. */
export const BETA_FEATURES_TAG = 'beta-features'
/** Ask AI chat, AI wands and the assistant generate endpoints. */
export const AI_ASSISTANT_TAG = 'beta-feature-ai-assistant'
/** "Live stock in AI search" integration — public per-operator stock feed for AI assistants. */
export const AI_SEARCH_TAG = 'beta-feature-ai-search'
/** "Buy X, get Y" (BOGO) promotion type in the Platform create promotion form. */
export const BOGO_PROMOS_TAG = 'beta-platform-promos-bogo'
/**
 * The cabinet service-status experience: Health dashboard, SMS notifications,
 * top-nav notification bell, out-of-service banner and the Operational /
 * Out of service cabinet statuses.
 */
export const CABINET_HEALTH_TAG = 'beta-feature-cabinet-health'
/** Per-cabinet pricing on recipes. */
export const CABINET_PRICING_TAG = 'beta-feature-cabinet-pricing'
/** Canva design studio popup on the Marketing page. */
export const CANVA_TAG = 'beta-feature-canva'
/** Settings → Enterprise API page and token management. */
export const ENTERPRISE_API_TAG = 'beta-feature-enterprise-api'
/** MCP server integration — remote-MCP connect via Micromart sign-in. */
export const ENTERPRISE_MCP_TAG = 'beta-feature-enterprise-mcp'
/** Cabinet e-tag design selector and preview. */
export const ETAG_DESIGNS_TAG = 'beta-feature-etag-designs'
/** Google Business Profile card and settings page. */
/** Location Insights (traffic → engagement → purchase funnel) and the cabinet health Traffic tab. */
export const FOOT_TRAFFIC_TAG = 'beta-feature-foot-traffic'
export const GOOGLE_BUSINESS_TAG = 'beta-feature-google-business'
/** Location Finder app card and the /dashboard/grow launch path. */
export const GROW_TAG = 'beta-feature-grow'
/** "Included with Micromart" card and its Revenue Protect, Shield, Guardian and Interac pages. */
export const INCLUDED_TAG = 'beta-feature-included'
/** Settings → Apps & integrations marketplace section. Nested api/mcp/quickbooks pages keep their own tags. */
export const INTEGRATIONS_TAG = 'beta-feature-integrations'
/** Platform inventory page (summary, filters, expansion rows, pricing workflow). */
export const INVENTORY_TAG = 'beta-feature-inventory'
/** Location analysis card on the store details page. */
export const LOCATION_ANALYSIS_TAG = 'beta-feature-location-analysis'
/** Market Insights cross-operator benchmarks. */
export const MARKET_INSIGHTS_TAG = 'beta-feature-market-insights'
/** POS passcode self-service settings page. */
export const PASSCODE_TAG = 'beta-feature-passcode'
/** Price tag preview in the Platform promotion form summary. */
export const PROMOTION_PRICE_TAG_PREVIEW_TAG = 'beta-feature-promotion-price-tag-preview'
/** QuickBooks Online integration — gates the card, /settings/integrations/quickbooks, and /api/apps/quickbooks/*. */
export const QUICKBOOKS_TAG = 'beta-feature-quickbooks'
/** Groups "Recently resolved" by cabinet on the Health overview page. */
export const RESOLVED_BY_CABINET_TAG = 'beta-feature-resolved-by-cabinet'
/** Micromart Shield card on Transactions and GET /api/metrics/shield/summary. */
export const SHIELD_TAG = 'beta-feature-shield'
/** Cabinet Controls tab "Restart cabinet" tools: run check, smart restart, power cycle. */
export const SMART_RESTART_TAG = 'beta-feature-smart-restart'
/** Internal live top-display preview. */
export const TOP_DISPLAY_PREVIEW_TAG = 'beta-feature-top-display-preview'
/** Per-promotion top-display visibility. */
export const TOP_DISPLAY_PROMOS_TAG = 'beta-feature-top-display-promos'
/** NAMA VDI 2.0 integration — settings integrations card and VDI settings page. */
export const VDI_TAG = 'beta-feature-vdi'
/** Vision reviewer beta pages in the Classic console. */
export const VISION_REVIEWER_BETA_TAG = 'beta-feature-vision-reviewer'

export interface FeatureFlag {
	tag: string
	name: string
	description: string
	/** No code references it anymore — kept so stale tags can still be seen and removed. */
	legacy?: boolean
}

/** All `beta-feature-*` user tags, for the Classic feature-flags page and seed data. */
export const FEATURE_FLAGS: FeatureFlag[] = [
	{
		tag: BETA_FEATURES_TAG,
		name: 'Beta features (umbrella)',
		description: 'Legacy umbrella beta flag. A few older gates still accept it; new features use their own granular tag below.',
	},
	{
		tag: AI_ASSISTANT_TAG,
		name: 'Ask AI assistant',
		description: 'Ask AI chat, AI wands and the assistant generate endpoints.',
	},
	{
		tag: AI_SEARCH_TAG,
		name: 'Live stock in AI search',
		description: 'Public per-operator live stock feed for ChatGPT and other AI assistants — integrations row, settings page and /api/ai-search/* endpoints.',
	},
	{
		tag: BOGO_PROMOS_TAG,
		name: 'Buy X, get Y promos',
		description: '"Buy X, get Y" (BOGO) promotion type in the Platform create promotion form.',
	},
	{
		tag: CABINET_HEALTH_TAG,
		name: 'Cabinet health',
		description: 'Health dashboard, SMS notifications, top-nav notification bell, out-of-service banner and the Operational / Out of service cabinet statuses.',
	},
	{
		tag: CABINET_PRICING_TAG,
		name: 'Cabinet pricing',
		description: 'Per-cabinet pricing on recipes.',
	},
	{
		tag: CANVA_TAG,
		name: 'Canva design studio',
		description: 'Canva design popup on the Marketing page.',
	},
	{
		tag: ENTERPRISE_API_TAG,
		name: 'Enterprise API',
		description: 'Settings → Enterprise API page and token management.',
	},
	{
		tag: ENTERPRISE_MCP_TAG,
		name: 'Enterprise MCP',
		description: 'MCP server integration — remote MCP connect via Micromart sign-in.',
	},
	{
		tag: ETAG_DESIGNS_TAG,
		name: 'E-tag designs',
		description: 'Cabinet e-tag design selector and preview.',
	},
	{
		tag: FOOT_TRAFFIC_TAG,
		name: 'Foot traffic',
		description: 'Location Insights (traffic → engagement → purchase funnel) and the cabinet health Traffic tab.',
	},
	{
		tag: GOOGLE_BUSINESS_TAG,
		name: 'Google Business Profile',
		description: 'Google Business Profile card and settings page.',
	},
	{
		tag: GROW_TAG,
		name: 'Grow apps',
		description: 'Gates the Location Finder app card and the /dashboard/grow launch path.',
	},
	{
		tag: INCLUDED_TAG,
		name: 'Included with Micromart',
		description: '"Included with Micromart" card and its Revenue Protect, Shield, Guardian and Interac pages.',
	},
	{
		tag: INTEGRATIONS_TAG,
		name: 'Apps & integrations',
		description: 'Settings → Apps & integrations marketplace section.',
	},
	{
		tag: INVENTORY_TAG,
		name: 'Inventory',
		description: 'Inventory page: summary, filters, expansion rows and pricing workflow.',
	},
	{
		tag: LOCATION_ANALYSIS_TAG,
		name: 'Location analysis',
		description: 'Location analysis card on the store details page.',
	},
	{
		tag: MARKET_INSIGHTS_TAG,
		name: 'Market Insights',
		description: 'Market Insights cross-operator benchmarks.',
	},
	{
		tag: PASSCODE_TAG,
		name: 'POS passcode',
		description: 'POS passcode self-service settings page.',
	},
	{
		tag: QUICKBOOKS_TAG,
		name: 'QuickBooks',
		description: 'QuickBooks Online integration card and settings page.',
	},
	{
		tag: PROMOTION_PRICE_TAG_PREVIEW_TAG,
		name: 'Promotion price tag preview',
		description: 'Price tag preview in the Platform promotion form summary.',
	},
	{
		tag: RESOLVED_BY_CABINET_TAG,
		name: 'Resolved by cabinet',
		description: 'Groups "Recently resolved" by cabinet on the Health overview page.',
	},
	{
		tag: SHIELD_TAG,
		name: 'Micromart Shield',
		description: 'Micromart Shield card on Transactions and the Shield summary endpoint.',
	},
	{
		tag: SMART_RESTART_TAG,
		name: 'Smart restart',
		description: 'Cabinet Controls tab "Restart cabinet" tools: run check, smart restart, power cycle.',
	},
	{
		tag: TOP_DISPLAY_PREVIEW_TAG,
		name: 'Top display preview',
		description: 'Internal live top-display preview.',
	},
	{
		tag: TOP_DISPLAY_PROMOS_TAG,
		name: 'Top display promos',
		description: 'Per-promotion top-display visibility.',
	},
	{
		tag: VDI_TAG,
		name: 'NAMA VDI 2.0',
		description: 'NAMA VDI 2.0 integration card and VDI settings page.',
	},
	{
		tag: VISION_REVIEWER_BETA_TAG,
		name: 'Vision reviewer beta',
		description: 'Vision reviewer beta pages in the Classic console.',
	},
]
